import { useLingui } from '@lingui/react/macro';
import { useRouteContext } from '@tanstack/react-router';
import { cn } from '@worldcoo/ui/lib/utils';
import { openUsercentricsPrivacySettings } from '@worldcoo/ui/lib/usercentrics';

/**
 * Footer control that reopens the cookie banner's settings layer.
 *
 * Shared across all four footers — the landing, the redesigned landing, a portal and the NGO area — so
 * the four never drift on label, behaviour or the CMP API they call. `className` is the only knob,
 * because each footer styles its own links differently.
 *
 * A `<button>`, not the `<a href="#">` the CMP's docs suggest: this opens a dialog rather than
 * navigating, so an anchor would add a bogus history entry, move focus to the top of the page, and read
 * as a link to assistive technology.
 */
export function PrivacySettingsLink(props: { className?: string }) {
	const { isInIframe } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();

	// An embed never loads the CMP (see the `isInIframe` branch in `__root.tsx`), so there is nothing here
	// for this to open: it would render as a control that logs an error and does nothing, for the whole
	// visit. Consent in an embed belongs to the platform hosting the iframe, and so does the control for
	// revisiting it. The portal footer's own `iframe` search-param check does not cover this, because the
	// server also infers `isInIframe` from `Sec-Fetch-*` for a cross-site embed that arrived without the
	// param — which is precisely a page that renders the footer with no CMP behind it.
	if (isInIframe) return null;

	return (
		<button type="button" onClick={openUsercentricsPrivacySettings} className={cn('cursor-pointer', props.className)}>
			{t`Privacy Settings`}
		</button>
	);
}
